import { CommonModule } from "@angular/common";
import { Component, computed, inject, model, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
    CenterUserService,
    DocumentService,
    DocumentTemplateCategoryService,
    FixDocumentService,
} from "@malirang/api-client";
import { SegmentTabOption, SegmentTabsComponent } from "@malirang/app-share";
import { DocumentInServiceType, DocumentStatus } from "@malirang/common";
import {
    AlertService,
    Button,
    Checkbox,
    Icon,
    Input,
    ModalService,
    ToastService,
} from "@malirang/front-share";
import { injectQueryParams } from "ngxtension/inject-query-params";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { CenterStore } from "../../../../../../stores/center/center.store";
import { CenterRoleStore } from "../../../../../../stores/center/role.store";
import { EtcDocumentCard } from "../../../components/etc-document-card/etc-document-card";
import { DocumentCard } from "../../../components/fix-document/fix-document-card/document-card";
import { DocumentCardParams } from "../../../components/pre-document/pre-document-card/pre-document-card";
import { PreDocumentList } from "../../../components/pre-document/pre-document-list/pre-document-list";
import {
    AddDocumentModal,
    AddDocumentModalProps,
} from "../../../modals/add-document/add-document.modal";
import {
    DocumentShareModal,
    DocumentShareModalProps,
} from "../../../modals/document-share/document-share.modal";
import { DocumentStore } from "../../../store/document.store";
import { DocumentBoxAccordian } from "./document-box-accordian/document-box-accordian";
import { environment } from "../../../../../../env/env";

/**
 * 문서함
 */
@Component({
    selector: "app-document-box",
    templateUrl: "./document-box.component.html",
    styleUrls: ["./document-box.component.css"],
    imports: [
        CommonModule,
        Input,
        Checkbox,
        Button,
        Icon,
        PreDocumentList,
        DocumentCard,
        DocumentBoxAccordian,
        EtcDocumentCard,
        SegmentTabsComponent,
    ],
})
export class DocumentBoxComponent {
    readonly centerUserService = inject(CenterUserService);
    readonly categoryService = inject(DocumentTemplateCategoryService);
    readonly modalService = inject(ModalService);
    readonly alertService = inject(AlertService);
    readonly documentService = inject(DocumentService);
    readonly fixDocumentService = inject(FixDocumentService);
    readonly toastService = inject(ToastService);
    readonly ticketId = injectQueryParams("ticketId");
    readonly documentId = injectQueryParams("documentId");
    readonly router = inject(Router);
    readonly ticketId$ = toObservableSignal(this.ticketId);
    readonly documentStore = inject(DocumentStore);
    readonly centerStore = inject(CenterStore);
    readonly centerRoleStore = inject(CenterRoleStore);

    editRole = this.documentStore.editRole;
    deleteRole = this.documentStore.deleteRole;
    fixDocuments = this.documentStore.fixDocuments;
    etcDocuments = this.documentStore.etcDocuments;

    // 검색어 필터가 적용된 기타 문서
    filteredEtcDocuments = computed(() => {
        const searchQuery = this.query()?.trim().toLowerCase();
        if (!searchQuery) {
            return this.etcDocuments();
        }
        return this.etcDocuments().filter(
            (doc) =>
                doc.etcType?.toLowerCase().includes(searchQuery) ||
                doc.template?.name?.toLowerCase().includes(searchQuery),
        );
    });

    haxNext = signal(false);
    cursor = signal("");
    query = model("");
    tab = model<string>("all");
    totalCount = signal(0);

    isProduction = false;

    categories = model<string[]>([]);
    teachers = model<string[]>([]);
    status = model<keyof typeof DocumentStatus | "ALL">("ALL");

    isVoucher = computed(() => !!this.documentStore.currentTicket()?.voucher);

    tabOptions: SegmentTabOption[] = [
        { label: "전체", value: "all" },
        { label: "필수", value: "required" },
        { label: "선택", value: "optional" },
    ];

    statusOptions = [
        { label: "전체", value: "ALL" },
        { label: "작성 완료", value: "COMPLETED" },
        { label: "작성 대기", value: "PENDING" },
    ];

    inDocuments = computed<DocumentCardParams[]>(() => {
        const 장단기계획서 = this.fixDocuments().find(
            (v) => v.inServiceType === "장단기계획서",
        );
        const 중간보고서 = this.fixDocuments().find(
            (v) => v.inServiceType === "중간보고서",
        );
        const 종결보고서 = this.fixDocuments().find(
            (v) => v.inServiceType === "종결보고서",
        );

        const allItems: DocumentCardParams[] = [
            {
                name: DocumentInServiceType.장단기계획서,
                value: "장단기계획서",
                phase: "IN_SERVICE",
                status: 장단기계획서?.status || "PENDING",
                updatedAt: 장단기계획서?.updatedAt
                    ? new Date(장단기계획서?.updatedAt!)
                    : undefined,
                document: 장단기계획서,
                lock: false,
            },
            {
                name: DocumentInServiceType.중간보고서,
                value: "중간보고서",
                phase: "IN_SERVICE",
                status: 중간보고서?.status || "PENDING",
                updatedAt: 중간보고서?.updatedAt
                    ? new Date(중간보고서?.updatedAt!)
                    : undefined,
                document: 중간보고서,
                lock: false,
            },
            {
                name: DocumentInServiceType.종결보고서,
                value: "종결보고서",
                phase: "IN_SERVICE",
                status: 종결보고서?.status || "PENDING",
                updatedAt: 종결보고서?.updatedAt
                    ? new Date(종결보고서?.updatedAt!)
                    : undefined,
                document: 종결보고서,
                lock: false,
            },
        ];

        // 검색어 필터 적용
        const searchQuery = this.query()?.trim().toLowerCase();
        if (searchQuery) {
            return allItems.filter((item) =>
                item.name.toLowerCase().includes(searchQuery),
            );
        }
        return allItems;
    });

    inDocumentsVoucher = computed<DocumentCardParams[]>(() => {
        const 발달재활서비스계약서 = this.fixDocuments().find(
            (v) => v.inServiceType === "발달재활서비스계약서",
        );
        const 아청심서비스계약서 = this.fixDocuments().find(
            (v) => v.inServiceType === "아청심서비스계약서",
        );
        const 개인정보수집및이용동의서 = this.fixDocuments().find(
            (v) => v.inServiceType === "개인정보수집및이용동의서",
        );
        const 서비스일정표 = this.fixDocuments()
            .filter((v) => v.inServiceType === "서비스일정표")
            .reverse()[0];
        const 서비스제공기록지 = this.fixDocuments()
            .filter((v) => v.inServiceType === "서비스제공기록지")
            .reverse()[0];
        const 발달재활서비스제공계획서 = this.fixDocuments().find(
            (v) => v.inServiceType === "발달재활서비스제공계획서",
        );
        const 아청심서비스제공계획서 = this.fixDocuments().find(
            (v) => v.inServiceType === "아청심서비스제공계획서",
        );

        const allItems: DocumentCardParams[] = [
            {
                name: DocumentInServiceType.발달재활서비스계약서,
                value: "발달재활서비스계약서",
                phase: "IN_SERVICE",
                status: 발달재활서비스계약서?.status || "PENDING",
                updatedAt: 발달재활서비스계약서?.updatedAt
                    ? new Date(발달재활서비스계약서?.updatedAt!)
                    : undefined,
                document: 발달재활서비스계약서,
                lock: false,
            },
            {
                name: DocumentInServiceType.아청심서비스계약서,
                value: "아청심서비스계약서",
                phase: "IN_SERVICE",
                status: 아청심서비스계약서?.status || "PENDING",
                updatedAt: 아청심서비스계약서?.updatedAt
                    ? new Date(아청심서비스계약서?.updatedAt!)
                    : undefined,
                document: 아청심서비스계약서,
                lock: false,
            },
            {
                name: DocumentInServiceType.개인정보수집및이용동의서,
                value: "개인정보수집및이용동의서",
                phase: "IN_SERVICE",
                status: 개인정보수집및이용동의서?.status || "PENDING",
                updatedAt: 개인정보수집및이용동의서?.updatedAt
                    ? new Date(개인정보수집및이용동의서?.updatedAt!)
                    : undefined,
                document: 개인정보수집및이용동의서,
                lock: false,
            },
            {
                name: DocumentInServiceType.서비스일정표,
                value: "서비스일정표",
                phase: "IN_SERVICE",
                status: 서비스일정표?.status || "PENDING",
                updatedAt: 서비스일정표?.updatedAt
                    ? new Date(서비스일정표?.updatedAt!)
                    : undefined,
                document: 서비스일정표,
                lock: false,
            },
            {
                name: DocumentInServiceType.서비스제공기록지,
                value: "서비스제공기록지",
                phase: "IN_SERVICE",
                status: 서비스제공기록지?.status || "PENDING",
                updatedAt: 서비스제공기록지?.updatedAt
                    ? new Date(서비스제공기록지?.updatedAt!)
                    : undefined,
                document: 서비스제공기록지,
                lock: false,
            },
            {
                name: DocumentInServiceType.발달재활서비스제공계획서,
                value: "발달재활서비스제공계획서",
                phase: "IN_SERVICE",
                status: 발달재활서비스제공계획서?.status || "PENDING",
                updatedAt: 발달재활서비스제공계획서?.updatedAt
                    ? new Date(발달재활서비스제공계획서?.updatedAt!)
                    : undefined,
                document: 발달재활서비스제공계획서,
                lock: false,
            },
            {
                name: DocumentInServiceType.아청심서비스제공계획서,
                value: "아청심서비스제공계획서",
                phase: "IN_SERVICE",
                status: 아청심서비스제공계획서?.status || "PENDING",
                updatedAt: 아청심서비스제공계획서?.updatedAt
                    ? new Date(아청심서비스제공계획서?.updatedAt!)
                    : undefined,
                document: 아청심서비스제공계획서,
                lock: false,
            },
        ];

        // 검색어 필터 적용
        const searchQuery = this.query()?.trim().toLowerCase();
        if (searchQuery) {
            return allItems.filter((item) =>
                item.name.toLowerCase().includes(searchQuery),
            );
        }
        return allItems;
    });

    constructor() {
        this.documentStore.reload$.subscribe(() => {
            this.handleRefresh();
        });
    }

    handleRefresh() {
        this.documentStore.reloadEtcDocuments();
        this.documentStore.reloadFixDocuments();
        this.documentStore.reloadPreDocuments();
    }

    handleShare() {
        const modal = this.modalService.create<DocumentShareModalProps>(
            DocumentShareModal,
            {
                componentProps: {
                    documents: [],
                },
            },
        );

        modal.closed.subscribe((result) => {
            if (result) {
                this.handleRefresh();

                if (result === "open") {
                    setTimeout(() => {
                        this.handleShare();
                    }, 100);
                }
            }
        });
    }

    handleAddDocument() {
        const modal = this.modalService.create<AddDocumentModalProps>(
            AddDocumentModal,
            {
                componentProps: {
                    ticketId: this.ticketId()!,
                },
            },
        );

        modal.closed.subscribe((result) => {
            if (result) {
                this.handleRefresh();
            }
        });
    }

    handleCheck(ev: boolean, id: string) {}

    handleAllCheck(ev: boolean) {}
}
